import { useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { ProtectedRoute } from '../components/auth/index.js';
import {
  ImprovementCard,
  ImprovementForm,
  TriageGuidelinePanel,
} from '../components/features/improvements/index.js';
import { useAuth } from '../hooks/useAuth.js';
import { usePilabRequests } from '../hooks/usePilabRequests.js';
import type { ImprovementRequestFilter } from '../lib/api/pilab-requests.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PI Lab への改善要望の一覧ページ。要望を出す入口も同じページに置き、判断待ち・却下・実装中・反映済みと「自分が出したもの」で絞って読める。判断待ちのものはこの画面で判断でき、やると決めたものは bot が実装して PR を出す
 * @graph-connects usePilabRequests [calls] 要望の一覧取得
 * @graph-connects useAuth [calls] ログインユーザーのメールと判断権限を取得する
 * @graph-connects ImprovementForm [calls] 要望の入力フォーム
 * @graph-connects ImprovementCard [calls] 要望 1 件の表示と判断
 * @graph-connects TriageGuidelinePanel [calls] 振り分け基準の表示
 */
export const Route = createFileRoute('/improvements')({
  component: ImprovementsRoute,
});

/**
 * 絞り込みの選択肢。
 *
 * - `open`（既定）… 判断が付いていないものだけ。**何が「終わった」かは API が決める**
 *   ので、ここで状態名を並べて除外しない（状態が増えたときに既定の一覧から黙って消える）
 * - `all` … 絞らない
 * - それ以外 … その状態だけ
 * @graph-connects none
 */
const STATUS_FILTERS = [
  'open',
  'all',
  'needs_decision',
  'in_progress',
  'shipped',
  'rejected',
  'withdrawn',
  'moved',
  'pending',
] as const;

/** @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする */
function ImprovementsRoute(): ReactNode {
  return (
    <ProtectedRoute pageKey="improvements">
      <ImprovementsPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function ImprovementsPage(): ReactNode {
  const { t } = useTranslation();
  const auth = useAuth();
  const email = auth.user?.email ?? '';
  // 「この要望を実行してよいか」はプロダクトの判断なので CTO（admin）と PMO だけ
  const canDecide = auth.can('DECIDE_IMPROVEMENT_REQUEST');
  // チーム課題に移せるのは CTO と PMO チーム（判定は API。ここは見え方だけ）
  const canMove = auth.can('MOVE_IMPROVEMENT_REQUEST');
  // 既定は「自分が出した、まだ判断が付いていないもの」。一覧を開いて最初に見たいのは
  // 終わったものではなく、自分が押し返せるものだから
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>('open');
  const [mineOnly, setMineOnly] = useState(true);
  const [composing, setComposing] = useState(false);

  const filter: ImprovementRequestFilter = {
    ...(status === 'all' ? {} : status === 'open' ? { open: true } : { status }),
    ...(mineOnly && email !== '' ? { requestedBy: email } : {}),
  };
  const { data, isLoading, isError, refetch } = usePilabRequests(filter);
  const requests = data?.requests ?? [];

  const statusOptions: SelectOption[] = STATUS_FILTERS.map((value) => ({
    value,
    label:
      value === 'all' || value === 'open'
        ? t(`improvements.filters.${value}`)
        : t(`improvements.status.${value}`),
  }));

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t('improvements.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t('improvements.intro')}</p>
        </div>
        <Button
          type="button"
          size="sm"
          className="shrink-0"
          onClick={() => {
            setComposing(true);
          }}
        >
          {t('improvements.form.heading')}
        </Button>
      </div>

      <TriageGuidelinePanel />

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-52">
          <Select
            aria-label={t('improvements.filters.label')}
            options={statusOptions}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as (typeof STATUS_FILTERS)[number]);
            }}
            fullWidth
            size="sm"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Checkbox
            checked={mineOnly}
            onChange={(e) => {
              setMineOnly(e.target.checked);
            }}
          />
          {t('improvements.filters.mineOnly')}
        </label>
        {!isLoading && !isError && (
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('improvements.list.count', { count: requests.length })}
          </span>
        )}
      </div>

      {isError && (
        <Card>
          <CardContent>
            <p className="text-sm text-[var(--color-error)]">{t('improvements.list.error')}</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => {
                void refetch();
              }}
            >
              {t('improvements.list.retry')}
            </Button>
          </CardContent>
        </Card>
      )}

      {!isError && isLoading && (
        <Card>
          <CardContent>
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('improvements.list.loading')}
            </p>
          </CardContent>
        </Card>
      )}

      {!isError && !isLoading && requests.length === 0 && (
        <Card>
          <CardContent>
            <p className="text-sm text-[var(--color-text-muted)]">{t('improvements.list.empty')}</p>
          </CardContent>
        </Card>
      )}

      {!isError &&
        !isLoading &&
        requests.map((request) => (
          <ImprovementCard
            key={request.id}
            request={request}
            viewerEmail={email}
            canDecide={canDecide}
            canMove={canMove}
          />
        ))}

      <Modal
        open={composing}
        onClose={() => {
          setComposing(false);
        }}
        title={t('improvements.form.heading')}
        description={t('improvements.form.description')}
        size="lg"
      >
        <ImprovementForm
          onDone={() => {
            setComposing(false);
          }}
        />
      </Modal>
    </div>
  );
}
